// src/utils/export.ts
import xlsx from "node-xlsx";
import { formatDateString } from "@/utils/format";
import { format } from "@/utils/common";
import { $t } from "@/locales";
import { useUserStore } from "@/store/modules/user";
import type { UserInfo } from "@/typings/types";

export interface ExportOptions {
    data: any[];
    filename: string;
    fileFormat: "csv" | "xlsx";
}

interface FormattedPaymentData {
    [key: string]: string;
}
export const backendToUserInfoMapping: {
    [backendKey: string]: keyof UserInfo;
} = {
    // Direct mappings
    uuid: "uuid",
    avatar: "avatar",
    name: "name",
    username: "username",
    email: "email",
    phone: "phone",

    // Snake case to camel case mappings
    is_guest: "isGuest",
    is_authenticated: "isAuthenticated",
    user_agent: "userAgent",
    browser_digest: "browserDigest",
    api_tier: "apiTier",
    data_usage_consent: "dataUsageConsent",
    custom_agents: "customAgents",
    half_space_unicode: "halfSpaceUnicode",
    custom_agents_costs: "customAgentsCosts",
    guides: "guides",
    total_unit: "totalUnit",
    use_general_system_message: "useGeneralSystemMessage",
    general_system_message: "generalSystemMessage",
    referral_code: "referralCode",

    // Special subscription fields
    "subscription.subscription_type": "tier",
    "subscription.start_date": "subscriptionStartDate",
    "subscription.end_date": "subscriptionEndDate",
    subscription_end_date: "subscriptionEndDate",
    subscription_start_date: "subscriptionStartDate",
    user_tier: "tier",
    user_api_tier: "apiTier",
    exchange_rate: "currencyExchangeRate",
    total_paid_in_unit: "totalPaidInUnit",
};


export function updateUserInfoFromResponse(
    response: Api.Auth.PingResponse
): void {
    try {
        const userStore = useUserStore();
        // Create a batch of updates to apply at once
        const updates: Partial<UserInfo> = {};
        // let tierChanged = false;
        // console.log("updateUserInfoFromResponse response: ", response);

        // Process each field from the response using our mapping
        Object.entries(response).forEach(([backendKey, value]) => {
            // Skip null/undefined values
            if (value === undefined) return;
            // Handle user authentication status

            if (backendKey === "username" && value) {
                updates.username = value as string;
                updates.isAuthenticated = true;
            }

            // Map backend field name to userInfo property if mapping exists
            const userInfoKey = backendToUserInfoMapping[backendKey];
            if (userInfoKey) {
                if (
                    typeof value === "boolean" ||
                    typeof value === "number" ||
                    typeof value === "string" ||
                    Array.isArray(value) ||
                    typeof value === "object"
                ) {
                    // Use the mapped field name for the update
                    (updates as any)[userInfoKey] = value;
                }
            }
        });

        // Apply all updates at once if there are any changes
        if (Object.keys(updates).length > 0) {
            userStore.updateUserInfo(updates);
        }
    } catch (error) {
        console.error("Error updating user info:", error);
    }
}

const getTranslatedHeaders = () => ({
    id: $t("billing.paymentId"),
    date: $t("common.date"),
    status: $t("usage.status"),
    purchaseItem: $t("billing.purchaseItem"),
    amount: $t("billing.amount"),
    discount: $t("billing.discount"),
    bank_tracking_code: $t("billing.trackingCode"),
    paidAmount: $t("billing.paidAmount"),
    confirmationDate: $t("billing.confirmationDate"),
});

const getStatusTranslation = (status: string): string => {
    switch (status) {
        case "success":
            return $t("usage.success");
        case "pending":
            return $t("usage.pending");
        case "failed":
            return $t("usage.failed");
        default:
            return status;
    }
};

const getPurchaseItemTranslation = (item: string): string => {
    switch (item) {
        case "api":
            return $t("common.credit");
        case "subscription":
            return $t("common.subscription");
        case "credit":
            return $t("common.credit");
        default:
            return "-";
    }
};

const formatExportData = (data: any[]): FormattedPaymentData[] => {
    const headers = getTranslatedHeaders();

    return data.map((item) => ({
        [headers.id]: item.id,
        [headers.date]: formatDateString(item.created_at, "full"),
        [headers.status]: getStatusTranslation(
            item.confirmed ? "success" : item.bank_transaction?.status || "pending"
        ),
        [headers.purchaseItem]: getPurchaseItemTranslation(item.purchase_item),
        [headers.amount]: format(Number(item.amount), 0),
        [headers.discount]: format(Number(item.discount_amount), 0),
        [headers.bank_tracking_code]: item.bank_tracking_code || "N/A",
        [headers.paidAmount]: format(Number(item.paid_amount), 0),
        [headers.confirmationDate]: item.confirmed_at
            ? formatDateString(item.confirmed_at, "full")
            : "-",
    }));
};

const exportToCSV = async (
    data: FormattedPaymentData[],
    filename: string
): Promise<void> => {
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
        headers
            .map((header) => {
                const value = row[header];
                // Properly escape and quote values containing commas or quotes
                return `"${String(value).replace(/"/g, '""')}"`;
            })
            .join(",")
    );

    const csv = [headers.join(","), ...rows].join("\n");

    // Create blob and trigger download
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const exportToXLSX = async (
    data: FormattedPaymentData[],
    filename: string
): Promise<void> => {
    const headers = Object.keys(data[0]);

    // Define column widths (in characters)
    const colWidths = headers.map((header) => ({
        wch: Math.max(header.length, 15), // minimum width of 15 characters
    }));

    // Convert data to array format required by node-xlsx
    const rows = data.map((row) => headers.map((header) => row[header]));

    // Create worksheet data
    const worksheetData = [headers, ...rows];

    // Build xlsx with options
    const sheetOptions = {
        "!cols": colWidths,
    };

    const buffer = xlsx.build([
        {
            name: $t("billing.paymentHistory"),
            data: worksheetData,
            options: sheetOptions,
        },
    ]);

    // Create blob and trigger download - convert Buffer to Uint8Array for proper Blob compatibility
    const blob = new Blob([new Uint8Array(buffer)], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${filename}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const exportData = async ({
    data,
    filename,
    fileFormat,
}: ExportOptions): Promise<void> => {
    try {
        const formattedData = formatExportData(data);

        if (fileFormat === "csv") {
            return exportToCSV(formattedData, filename);
        }
        return exportToXLSX(formattedData, filename);
    } catch (error) {
        console.error(`Error exporting ${fileFormat} file:`, error);
        throw error;
    }
};
